// features/workstations/qqq/today/vehicle-decision-card.js
// ═══════════════════════════════════════════════════════════
// Vehicle Decision Card
// ═══════════════════════════════════════════════════════════
//
// Per docs/QQQ_DASHBOARD_REBUILD_SPEC.md Section II + Open Q #4.
// Per docs/QQQ_EVENT_DAY_PLAYBOOK.md Section V.
//
// Operationalizes the 100% benchmark Tim named today:
// "Usually for this $2 move on a stock and it is 0DTE, the option
// is at least 100% move already. Today is different."
//
// Consumes /api/qqq/vehicle/recommend (Phase 2b-2). Renders:
//   - Action: TAKE / SKIP / WAIT / TGT1_ONLY (color-coded)
//   - Specific recommendation: "BUY 5 contracts QQQ $659 0DTE
//     call at ~$3.62"
//   - Sizing math (collapsible)
//   - IV-crush recognition status
//   - 100% benchmark expectation (normal vs event-day)
//   - Composite + YC state inputs (drill-down)
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

const ACTION_TONE = {
  TAKE:        'sage',
  TGT1_ONLY:   'gold',
  SKIP:        'purple',
  WAIT:        'gold',
};

export default function VehicleDecisionCard({ ycState = null } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMath, setShowMath] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (ycState?.entry) params.set('yc_entry', String(ycState.entry));
      if (ycState?.stop)  params.set('yc_stop', String(ycState.stop));
      if (ycState?.tgt1)  params.set('yc_tgt1', String(ycState.tgt1));
      if (ycState?.tgt2)  params.set('yc_tgt2', String(ycState.tgt2));
      const url = `/api/qqq/vehicle/recommend${params.toString() ? '?' + params : ''}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [ycState?.entry, ycState?.stop, ycState?.tgt1, ycState?.tgt2]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <Card><div className="qqq-vehicle-card__loading">Loading recommendation…</div></Card>;
  if (error) return <Card><div className="qqq-vehicle-card__error">Error: {error}</div></Card>;

  const rec = data?.recommendation;
  if (!rec) {
    return <Card><div className="qqq-vehicle-card__loading">{data?.error || 'No recommendation available.'}</div></Card>;
  }

  const tone = ACTION_TONE[rec.action] || 'gold';

  return (
    <Card>
      <div className="qqq-vehicle-card__header">
        <div className="qqq-vehicle-card__title">Vehicle Decision</div>
        <button type="button" onClick={load} className="qqq-vehicle-card__refresh-btn">Refresh</button>
      </div>

      {/* Action banner */}
      <div className={`qqq-vehicle-card__action qqq-vehicle-card__action--${tone}`}>
        <div className="qqq-vehicle-card__action-label">{rec.action}</div>
        <div className="qqq-vehicle-card__action-direction">{rec.direction.toUpperCase()}</div>
        <div className="qqq-vehicle-card__action-vehicle">{vehicleLabel(rec.vehicle)}</div>
      </div>

      {/* Specific recommendation */}
      {rec.specific_recommendation && (
        <div className="qqq-vehicle-card__specific">
          {rec.specific_recommendation}
        </div>
      )}

      {/* Reason */}
      <div className="qqq-vehicle-card__reason">
        {rec.reason}
      </div>

      {/* Sizing math (collapsible) */}
      {rec.sizing_math && (
        <div className="qqq-vehicle-card__section">
          <button
            type="button"
            className="qqq-vehicle-card__section-toggle"
            onClick={() => setShowMath(s => !s)}
          >
            {showMath ? '▾' : '▸'} Sizing math
          </button>
          {showMath && (
            <ul className="qqq-vehicle-card__sizing-math">
              {rec.sizing_math.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* IV-crush recognition */}
      {rec.iv_crush_status && rec.iv_crush_status !== 'n/a' && (
        <div className={`qqq-vehicle-card__iv-crush qqq-vehicle-card__iv-crush--${rec.iv_crush_status}`}>
          <div className="qqq-vehicle-card__iv-crush-label">IV-Crush Recognition</div>
          <div className="qqq-vehicle-card__iv-crush-detail">{rec.iv_crush_detail}</div>
        </div>
      )}

      {/* 100% benchmark expectation */}
      {rec.benchmark_expectation && (
        <div className="qqq-vehicle-card__benchmark">
          <div className="qqq-vehicle-card__benchmark-label">100% Benchmark</div>
          <div className="qqq-vehicle-card__benchmark-line">
            <span className="qqq-vehicle-card__benchmark-tag qqq-vehicle-card__benchmark-tag--normal">Normal</span>
            {rec.benchmark_expectation.normal_day}
          </div>
          {rec.is_event_day && (
            <div className="qqq-vehicle-card__benchmark-line">
              <span className="qqq-vehicle-card__benchmark-tag qqq-vehicle-card__benchmark-tag--event">Event</span>
              {rec.benchmark_expectation.event_day}
            </div>
          )}
          <div className="qqq-vehicle-card__benchmark-vehicle-note">
            {rec.benchmark_expectation.vehicle_note}
          </div>
        </div>
      )}

      {/* Footer: composite + meta */}
      <div className="qqq-vehicle-card__footer">
        {rec.composite_summary && (
          <span className="qqq-vehicle-card__composite-tag">{rec.composite_summary}</span>
        )}
        {data?.options_chain_summary?.atm_call_strike != null && (
          <span className="qqq-vehicle-card__chain-tag">
            ATM call ${data.options_chain_summary.atm_call_strike}@${data.options_chain_summary.atm_call_premium?.toFixed(2)}
          </span>
        )}
      </div>
    </Card>
  );
}

function vehicleLabel(vehicle) {
  const labels = {
    atm_call:             'ATM CALL (naked)',
    atm_put:              'ATM PUT (naked)',
    debit_call_vertical:  'DEBIT CALL VERTICAL',
    debit_put_vertical:   'DEBIT PUT VERTICAL',
    calendar:             'CALENDAR SPREAD',
    shares:               'SHARES',
    skip:                 'NONE',
  };
  return labels[vehicle] || String(vehicle).toUpperCase();
}

function Card({ children }) {
  return <div className="qqq-vehicle-card">{children}</div>;
}
